import { lazy, useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Jwt } from '../utils/Jwt';
import { buildLoginPath, restoreCheckedState } from '../utils/authRedirect';
import { useGlobalContext } from '../contexts/GlobalContext';
import Spinner from '../components/@common/Spin';
import SessionRestoreError from '../components/@common/SessionRestoreError';

const Layout = lazy(() => import('components/@common/Layout'));

export default function ProtectedRoute() {
    const location = useLocation();
    const [restore, setRestore] = useState({ status: 'checking' });
    const attemptRef = useRef(0);
    const { setIsLoading } = useGlobalContext();
    const redirection = `${location.pathname}${location.search}`;

    const runRestore = useCallback(async () => {
        const attempt = ++attemptRef.current;
        setRestore({ status: 'checking' });
        setIsLoading(true);
        let result;
        try {
            result = await Jwt.restoreSession();
        } finally {
            if (attempt === attemptRef.current) setIsLoading(false);
        }
        // 해제됐거나 경로가 바뀌었거나 새 시도가 시작됐으면 늦은 결과를 적용하지 않는다
        if (attempt !== attemptRef.current) return;
        // 취소됐거나 그 사이 로그아웃·새 로그인이 생긴 결과는 버리고, 네트워크 없이 현재 확정된 access 상태만 반영한다
        if (result.status === 'cancelled' || !Jwt.isCurrentResult(result)) {
            setRestore(Jwt.readSessionState());
            return;
        }
        setRestore(result);
    }, [setIsLoading]);

    useEffect(() => {
        runRestore();
        return () => {
            attemptRef.current += 1;
        };
    }, [location, runRestore]);

    if (restore.status === 'checking') return <Spinner />;
    if (restore.status === 'authenticated') return <Layout />;
    // 일시 실패는 업무 내용을 아직 열지 않고 같은 경로에서 안내와 수동 재시도만 제공한다
    if (restore.status === 'failed') return <SessionRestoreError onRetry={runRestore} pending={false} />;
    if (restore.status === 'ended') {
        return <Navigate to={buildLoginPath({ reason: 'session-ended', redirection })} state={restoreCheckedState()} replace />;
    }
    return <Navigate to={buildLoginPath({ redirection })} state={restoreCheckedState()} replace />;
};
